// import React from 'react';
// import { motion } from 'motion/react';
// import { useInView } from 'react-intersection-observer';
// import { Zap, Shield, Users, ArrowRight } from 'lucide-react';

// export function WhatIsNFC() {
//   const [ref, inView] = useInView({
//     triggerOnce: true,
//     threshold: 0.2,
//   });

//   const benefits = [
//     {
//       icon: Zap,
//       text: 'Effortlessly transfer information',
//     },
//     {
//       icon: Shield,
//       text: 'Enable secure payments',
//     },
//     {
//       icon: Users,
//       text: 'Engage customers in a smarter way',
//     },
//   ];

//   return (
//     <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-20 relative">
//       <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent dark:via-primary/10" />

//       <div className="max-w-4xl mx-auto relative">
//         <motion.div
//           ref={ref}
//           initial={{ opacity: 0, y: 40 }}
//           animate={inView ? { opacity: 1, y: 0 } : {}}
//           transition={{ duration: 0.8 }}
//           className="text-center"
//         >
//           <motion.h2
//             initial={{ opacity: 0, y: 20 }}
//             animate={inView ? { opacity: 1, y: 0 } : {}}
//             transition={{ delay: 0.2 }}
//             className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6"
//           >
//             What is an{' '}
//             <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
//               NFC Card?
//             </span>
//           </motion.h2>

//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={inView ? { opacity: 1, y: 0 } : {}}
//             transition={{ delay: 0.4 }}
//             className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 lg:p-12 shadow-xl space-y-6"
//           >
//             <p className="text-lg text-muted-foreground leading-relaxed">
//               Whether you need NFC cards for access control, secure payments, or interactive marketing campaigns, 
//               we have the perfect solution. Our experts work closely with you to create custom NFC card designs 
//               that reflect your brand identity and achieve your business goals.
//             </p>

//             <div className="space-y-4 pt-4">
//               <p className="text-left font-semibold text-foreground text-lg">
//                 With NFC technology, you can:
//               </p>

//               <div className="grid sm:grid-cols-3 gap-4">
//                 {benefits.map((benefit, index) => (
//                   <motion.div
//                     key={index}
//                     initial={{ opacity: 0, scale: 0.9 }}
//                     animate={inView ? { opacity: 1, scale: 1 } : {}}
//                     transition={{ delay: 0.6 + index * 0.1 }}
//                     whileHover={{ y: -4, scale: 1.02 }}
//                     className="flex flex-col items-center gap-3 p-4 bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-primary/20 hover:border-primary/40 transition-all"
//                   >
//                     <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
//                       <benefit.icon className="w-6 h-6 text-white" />
//                     </div>
//                     <p className="text-sm text-muted-foreground text-center leading-snug">
//                       {benefit.text}
//                     </p>
//                   </motion.div>
//                 ))}
//               </div>
//             </div>

//             <motion.p
//               initial={{ opacity: 0 }}
//               animate={inView ? { opacity: 1 } : {}}
//               transition={{ delay: 0.9 }}
//               className="text-lg text-foreground font-medium pt-4"
//             >
//               Say goodbye to traditional cards and embrace the convenience of NFC technology.
//             </motion.p>
//           </motion.div>
//         </motion.div>
//       </div>
//     </section>
//   );
// }


import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Zap, Shield, Users } from "lucide-react";

const WhatIsNFC = () => {
    const [inView, setInView] = useState(false);

    // Simple scroll detection for animation
    useEffect(() => {
        const handleScroll = () => {
            const section = document.getElementById("what-is-nfc");
            if (section) {
                const rect = section.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8) {
                    setInView(true);
                }
            }
        };
        window.addEventListener("scroll", handleScroll);
        handleScroll(); // check immediately
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const benefits = [
        { icon: Zap, text: "Effortlessly transfer information" },
        { icon: Shield, text: "Enable secure payments" },
        { icon: Users, text: "Engage customers in a smarter way" },
    ];

    return (
        <section
            id="what-is-nfc"
            style={{
                position: "relative",
                paddingTop: "80px",
                paddingBottom: "80px",
                paddingLeft: "16px",
                paddingRight: "16px",
                overflow: "hidden",
                background: "linear-gradient(to bottom, transparent, rgba(99,102,241,0.05), transparent)",
            }}
        >
            <div
                style={{
                    maxWidth: "1024px",
                    margin: "0 auto",
                    position: "relative",
                }}
            >
                {/* Section Title */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    style={{ textAlign: "center" }}
                >
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.2 }}
                        style={{
                            fontSize: "2.5rem",
                            fontWeight: "bold",
                            marginBottom: "24px",
                            lineHeight: 1.2,
                            color: "#111",
                        }}
                    >
                        What is an{" "}
                        <span
                            style={{
                                background: "linear-gradient(to right, #6366f1, #22d3ee, #facc15)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            NFC Card?
                        </span>
                    </motion.h2>

                    {/* Card Container */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.4 }}
                        style={{
                            background: "rgba(255,255,255,0.5)",
                            backdropFilter: "blur(8px)",
                            border: "1px solid rgba(99,102,241,0.2)",
                            borderRadius: "24px",
                            padding: "32px",
                            boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                        }}
                    >
                        {/* Description */}
                        <p style={{ fontSize: "1.125rem", color: "#6b7280", lineHeight: 1.6 }}>
                            Whether you need NFC cards for access control, secure payments, or interactive marketing campaigns,
                            we have the perfect solution. Our experts work closely with you to create custom NFC card designs
                            that reflect your brand identity and achieve your business goals.
                        </p>

                        {/* Benefits */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            <p style={{ fontWeight: 600, color: "#111", fontSize: "1.125rem" }}>
                                With NFC technology, you can:
                            </p>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                    gap: "16px",
                                }}
                            >
                                {benefits.map((benefit, index) => {
                                    const Icon = benefit.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={inView ? { opacity: 1, scale: 1 } : {}}
                                            transition={{ delay: 0.6 + index * 0.1 }}
                                            whileHover={{ y: -4, scale: 1.02 }}
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: "12px",
                                                padding: "16px",
                                                background: "linear-gradient(to bottom right, rgba(99,102,241,0.05), rgba(34,211,238,0.05))",
                                                borderRadius: "16px",
                                                border: "1px solid rgba(99,102,241,0.2)",
                                                cursor: "pointer",
                                                transition: "all 0.3s",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "48px",
                                                    height: "48px",
                                                    borderRadius: "50%",
                                                    background: "linear-gradient(to bottom right, #6366f1, #22d3ee)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Icon style={{ width: "24px", height: "24px", color: "#fff" }} />
                                            </div>
                                            <p
                                                style={{
                                                    fontSize: "0.875rem",
                                                    color: "#6b7280",
                                                    textAlign: "center",
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {benefit.text}
                                            </p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Final CTA Text */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={inView ? { opacity: 1 } : {}}
                            transition={{ delay: 0.9 }}
                            style={{ fontSize: "1.125rem", color: "#111", fontWeight: 500, paddingTop: "16px" }}
                        >
                            Say goodbye to traditional cards and embrace the convenience of NFC technology.
                        </motion.p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default WhatIsNFC;

